import {Box} from '@dropbox/dig-foundations';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {queryFromParam} from 'components/search/box/GlobalSearchBox';
import {SearchBox} from 'components/search/box/SearchBox';
import {ROUTE_PATHS} from 'constant';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {useEffect, useState} from 'react';
import {Navigate, useSearchParams} from 'react-router';

import {useOpenOutsideInNewWindow} from './useOpenOutsideInNewWindow';
import {useSearchAppManifest} from './useSearchAppManifest';

/** Room around the box, so it still reads as the box. */
const INSET = 8;
/** The box's header (16 + 28 + 16, plus its hairline). */
const HEADER_HEIGHT = 61;
/** The installed window's size the first time it opens: the overlay box's, with the inset. */
const FIRST_WINDOW = {width: 1120 + 2 * INSET, height: 840};
const SIZED_KEY = 'search-app:sized';

const useWindowWidth = () => {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
};

/**
 * The first time the installed app opens, size its window to the box. After
 * that the browser remembers whatever size it's left at.
 */
const useFirstWindowSize = () => {
  useEffect(() => {
    if (!window.matchMedia('(display-mode: standalone)').matches) return;
    if (localStorage.getItem(SIZED_KEY)) return;
    localStorage.setItem(SIZED_KEY, '1');
    window.resizeTo(FIRST_WINDOW.width, FIRST_WINDOW.height);
  }, []);
};

const SearchAppPage = () => {
  const [searchParams] = useSearchParams();
  const [query, setQuery] = useState(() => queryFromParam(searchParams.get('s')));
  const width = useWindowWidth();
  useSearchAppManifest();
  useOpenOutsideInNewWindow(ROUTE_PATHS.SEARCH_APP);
  useFirstWindowSize();
  useDocumentTitle(t('searching') + (query.length > 0 ? ` "${query}"` : '...'));

  useEffect(() => {
    analyticsLogger().logEvent('SEARCH_SHOWN', {
      source: window.matchMedia('(display-mode: standalone)').matches ? 'app' : 'app_browser',
    });
  }, []);

  // There's nothing to close here, so Escape clears the field instead (the
  // box's own Escape, closing the pane, comes first and stops there).
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) setQuery('');
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <Box padding="8" backgroundColor="Background Subtle" style={{minHeight: '100vh'}}>
      <SearchBox
        query={query}
        setQuery={setQuery}
        logSelections
        width={width - 2 * INSET}
        bodyHeight={`calc(100vh - ${HEADER_HEIGHT + 2 * INSET}px)`}
      />
    </Box>
  );
};

/**
 * The search box on its own page, installable as its own small app (a
 * window with just search and Ask). Behind `ask_relaunch`; without it, home.
 */
export const SearchApp = () => {
  const {isAskRelaunchEnabled} = useAtomValue(growthbookCacheAtom);
  return isAskRelaunchEnabled ? <SearchAppPage /> : <Navigate to={ROUTE_PATHS.HOME} replace />;
};
